import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Input,
  Row,
  Badge,
} from "reactstrap";
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Popover, Whisper } from "rsuite";

import GalleryPreviewModal from "../../../views/library/LibraryComponents/FileInformation/GalleryPreviewModal";
import DropdownOptionWithIcon from "../../../components/Menus/DropdownOptionWithIcon";
import useDeleteRecordFile from "../../../api/api-hooks/images/useDeleteRecordFile";
import UppyFile, {
  UppyFileInterface,
} from "../../../components/Inputs/UppyFile";
import PopoverDropdownMenu from "../../../components/Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../../components/Menus/DropdownMenuDivider";
import useUpdateFile from "../../../api/api-hooks/images/useUpdateFile";
import { deleteFileBlobStorage } from "../../../utils/FileUtils";
import { FileInterface } from "../../../types/RecordInterfaces";
import useErrorMessage from "../../../hooks/useErrorMessage";
import { FileRecord } from "../../../views/library/Library";
import AppSpinner from "../../../elements/AppSpinner";
import useToggle from "../../../hooks/useToggle";
import FileWithOptions from "./FileWithOptions";
import DisplayRecord from "../DisplayRecord";
import EmptyField from "../EmptyField";

interface Props {
  field: any;
  existingValues: FileInterface[];
  recordID: string;
  mode: string;
  tableView: boolean;
  modifyTrigger: number;
  moduleAPIName: string;
  onUpdate: (args: any) => void;
  submitting: boolean;
  error: boolean;
  subitemID?: string;
}

const AttachmentsRecord = (
  {
    field,
    existingValues,
    recordID,
    mode,
    tableView,
    modifyTrigger,
    moduleAPIName,
    onUpdate,
    submitting,
    error,
    subitemID,
  }: Props,
  ref: React.ForwardedRef<any> | undefined
) => {
  const [addOpen, setAddOpen] = useState<boolean>(!existingValues?.length);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editFile, setEditFile] = useState<UppyFileInterface | null>(null);
  const [editingFile, setEditingFile] = useState<boolean>(false);
  const [newFileName, setNewFileName] = useState<string>("");
  const [values, setValues] = useState<FileInterface[]>([
    ...(Array.isArray(existingValues) ? existingValues : []),
  ]);
  const { error: localError } = useErrorMessage();
  const [openPreview, toggleOpenPreview] = useToggle(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [openInfo, setOpenInfo] = useState<FileRecord | null>(null);
  const { updateFile, updatingFile } = useUpdateFile();
  const { deleteRecordFile } = useDeleteRecordFile();

  const whisperRef = useRef<any>();

  const startEditing = () => {
    setIsEditing(true);
    whisperRef.current?.open();
  };

  const handleOpenEditFile = (value: UppyFileInterface, fileRef: any) => {
    fileRef.current?.close();
    setEditFile(value);
    setNewFileName(value.Name);
    setEditingFile(true);
  };

  const handleUpdateName = () => {
    if (newFileName === "" || !editFile) return;
    setValues((prev) => {
      return prev.map((val) => {
        if (val === editFile) {
          return {
            ...val,
            Name: newFileName,
          };
        }

        return val;
      });
    });

    updateFile({
      payload: {
        Name: newFileName,
      },
      fileID: editFile?.RecordID,
    });

    setEditingFile(false);
    setNewFileName("");
  };

  const handleAfterSubmit = (result: any) => {
    onUpdate({
      field,
      recordID,
      payload: {
        [field.APIName]: [...values, ...result],
      },
    });

    setValues((prev) => {
      return [...prev, ...result];
    });
  };

  const save = (newValues = values) => {
    try {
      // Don't save if nothing was updated.
      if (JSON.stringify(newValues) === JSON.stringify(existingValues)) {
        return;
      }
    } catch (err) {
      return;
    }

    onUpdate({
      field,
      recordID,
      payload:
        newValues !== null
          ? { [field.APIName]: [...newValues] }
          : { [field.APIName]: null },
    });
  };

  const FileOptions = ({
    value,
    index,
    fileRef,
  }: {
    value: UppyFileInterface;
    index: number;
    fileRef: any;
  }) => {
    return (
      <PopoverDropdownMenu>
        <h5
          style={{ maxWidth: 250 }}
          className="px-2 pt-2 text-dark text-truncate"
        >
          {value.Name}
        </h5>
        <DropdownMenuDivider></DropdownMenuDivider>

        <DropdownOptionWithIcon
          name="Open Preview"
          icon="mdi mdi-file-find"
          onClick={() => {
            toggleOpenPreview();
            setSelected(index);
          }}
        />
        <DropdownOptionWithIcon
          name="Rename File"
          icon="mdi mdi-pencil"
          onClick={() => handleOpenEditFile(value, fileRef)}
        />

        <DropdownMenuDivider></DropdownMenuDivider>
        <DropdownOptionWithIcon
          name="Delete File"
          icon="mdi mdi-delete"
          onClick={() =>
            deleteFileBlobStorage({
              filePath: value.File,
              recordID,
              moduleAPIName,
              fieldAPIName: field.SubitemParent
                ? `${field.SubitemParent}.${field.APIName}`
                : field.APIName,
              subitemID,
              onSuccess: () => {
                setValues((prev) => {
                  const newValues = prev.filter(
                    (val) => val.File !== value.File
                  );
                  save(newValues);
                  fileRef.current?.close();
                  return newValues;
                });
              },
            })
          }
        />
      </PopoverDropdownMenu>
    );
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <>
      <Whisper
        preventOverflow
        ref={whisperRef}
        placement="autoVertical"
        trigger={isEditing && !optionsOpen && !keepOpen ? "click" : "none"}
        speaker={
          <Popover
            style={{ width: "350px" }}
            className={`${openPreview ? "d-none" : ""} sidebar-popover p-0`}
          >
            <Card className="mb-0">
              <CardBody className="cursor-pointer p-2 text-dark">
                <Row className="align-items-center">
                  <Col xs="" className="d-flex">
                    <h5 className="text-dark m-0 pr-2">{field?.Name}</h5>
                    <Badge color="light">
                      {field?.Public ? "Public" : "Private"}
                    </Badge>
                  </Col>
                  <Col xs="auto">
                    <Button
                      size="sm"
                      outline
                      color="secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        save();
                        whisperRef?.current?.close();
                      }}
                    >
                      Done
                    </Button>
                  </Col>
                </Row>
              </CardBody>
              <CardBody className="p-0">
                {values ? (
                  values.map((file, i) => {
                    return (
                      <FileWithOptions
                        key={i}
                        item={file}
                        index={i}
                        isFirst={true}
                        setOptionsOpen={setOptionsOpen}
                        FileOptions={FileOptions}
                        openPreview={openPreview}
                        toggleOpenPreview={() => {
                          toggleOpenPreview();
                          setSelected(i);
                        }}
                      />
                    );
                  })
                ) : (
                  <span>--</span>
                )}
              </CardBody>
              <CardBody
                onClick={() => [
                  editingFile ? setEditingFile(false) : setAddOpen(!addOpen),
                ]}
                className={`cursor-pointer p-2 bg-ultralight--hover text-dark border-top border-superlight ${
                  addOpen && "bg-ultralight"
                }`}
              >
                <Row className="align-items-center">
                  <Col xs="">
                    {editingFile ? (
                      <h5 className="text-dark m-0">Edit File</h5>
                    ) : (
                      <h5 className="text-dark m-0">Add New File</h5>
                    )}
                  </Col>
                  <Col xs="auto" className="pl-0">
                    <Button
                      size="sm"
                      outline
                      className="btn-icon-only"
                      style={{
                        height: "1.5rem",
                        width: "1.5rem",
                      }}
                    >
                      <i
                        className={`mdi mdi-chevron-${
                          addOpen || editingFile ? "down" : "right"
                        }`}
                        style={{
                          fontSize: 20,
                          lineHeight: 1,
                          position: "relative",
                          top: 1,
                        }}
                      />
                    </Button>
                  </Col>
                </Row>
              </CardBody>
              <Collapse isOpen={addOpen || editingFile}>
                <div className={`p-2 border-top border-superlight`}>
                  <Row className="align-items-center">
                    {editingFile ? (
                      <>
                        <Col xs="" className="pr-2">
                          <Input
                            value={newFileName}
                            bsSize="sm"
                            onChange={(e) => setNewFileName(e.target.value)}
                          />
                        </Col>
                        <Col xs="auto" className="pl-0">
                          <Button
                            size="sm"
                            color="primary"
                            className="btn-icon-only"
                            onClick={() => handleUpdateName()}
                          >
                            <i className="mdi mdi-pencil"></i>
                          </Button>
                        </Col>
                      </>
                    ) : (
                      <Col>
                        <UppyFile
                          afterSubmit={(result) => handleAfterSubmit(result)}
                          multiFiles={true}
                          meta={{ isPublic: !!field?.Public }}
                          setKeepOpen={setKeepOpen}
                          filesToFilter={values}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row>
                    <Col>
                      {(localError || error) && (
                        <div className="mb-0 text-left">
                          <p className="m-0 text-danger">
                            {error && (
                              <small>
                                Unable to save your changes. Try again.
                              </small>
                            )}
                            {localError && <small>{localError}</small>}
                          </p>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              </Collapse>
            </Card>
          </Popover>
        }
      >
        <div>
          <DisplayRecord
            mode={mode as any}
            tableView={tableView}
            modifyTrigger={modifyTrigger as any}
            openModify={startEditing}
            isSensitive={field?.Sensitive ?? false}
            displayComponent={(_: any, innerRef: any) => {
              if (submitting) return <AppSpinner size="sm" />;
              return (
                <div
                  className="d-flex flex-column"
                  style={{ maxWidth: 350 }}
                  ref={innerRef}
                >
                  {values.length > 0 ? (
                    values?.map((file, i) => (
                      <span className="text-truncate" key={i}>
                        <i className="mdi mdi-file mr-1"></i>
                        {file?.Name}
                      </span>
                    ))
                  ) : (
                    <EmptyField table={tableView} />
                  )}
                </div>
              );
            }}
          />
          {values && selected !== null && (
            <GalleryPreviewModal
              isOpen={openPreview}
              // @ts-expect-error
              files={
                Array.isArray(values)
                  ? values.map((f) => {
                      return { id: f.RecordID, _id: f.RecordID, File: f };
                    })
                  : []
              }
              selected={selected}
              nextFile={() => {
                setSelected((prev) =>
                  prev === values.length - 1 ? 0 : (prev || 0) + 1
                );
              }}
              previousFile={() => {
                setSelected((prev) =>
                  prev === 0 ? values.length - 1 : (prev || 0) - 1
                );
              }}
              deleteFile={handleFileDeletion}
              submitting={updatingFile}
              handleUpdateFile={handleUpdateFile}
              setSelected={setSelected}
              closeModal={() => {
                toggleOpenPreview();
                whisperRef.current?.open();
              }}
              setOpenInfo={setOpenInfo}
              openInfo={openInfo}
            />
          )}
        </div>
      </Whisper>
    </>
  );

  function handleUpdateFile(
    newValue: string,
    key: string,
    onSuccess?: () => void
  ) {
    if (!values || typeof selected !== "number") return;

    updateFile({
      payload: { [key]: newValue },
      fileID: values[selected]?.RecordID ?? "",
      onSuccess: () => {
        setValues((prev) => {
          return prev.map((f, i) => {
            if (i === selected) {
              const newF = structuredClone(f) as any;

              newF[key] = newValue;

              return newF;
            }

            return f;
          });
        });

        onSuccess && onSuccess();
      },
    });
  }

  function handleFileDeletion(fileID: string) {
    deleteRecordFile({
      body: {
        fileID: fileID,
        moduleAPIName: moduleAPIName,
        recordID,
        fieldAPIName: field.SubitemParent
          ? `${field.SubitemParent}.${field.APIName}`
          : field.APIName,
        type: "fileID",
      },
      onSuccess: () => {
        setValues((prev) => {
          setSelected((prev) =>
            prev === values.length - 1 ? 0 : (prev || 0) + 1
          );
          return prev.filter((val) => val.File !== fileID);
        });
      },
    });
  }
};

export default forwardRef(AttachmentsRecord);
